/**
 * Ticket workflow-template constants (SOUND-506).
 *
 * Mirrors the backend contract (validators/ticketTemplateValidator.mjs +
 * config/statusMachines.mjs) so the editor can constrain input and surface
 * validation before a round trip. The server re-validates on save/activate.
 */

// Locked v1 intake field types (design of record §1.1).
export const INTAKE_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "date",
  "select",
  "select-other",
  "boolean",
  "mid-ref",
  "company-ref",
];

// Accepted by the schema shape but rejected on save until wired — the editor
// lists them as "coming soon" (disabled).
export const INTAKE_FIELD_TYPES_RESERVED = ["dba-ref", "user-ref", "file"];

export const INTAKE_TYPE_LABELS = {
  text: "Text",
  textarea: "Long text",
  number: "Number",
  date: "Date",
  select: "Dropdown",
  "select-other": "Dropdown + other",
  boolean: "Yes / No",
  "mid-ref": "MID reference",
  "company-ref": "Company reference",
  "dba-ref": "DBA reference (soon)",
  "user-ref": "User reference (soon)",
  file: "File (soon)",
};

// Types whose value is a controlled option list.
export const OPTION_TYPES = ["select", "select-other"];

export const VISIBLE_WHEN_OPERATORS = ["eq", "neq", "in", "notIn"];
export const OPERATOR_LABELS = {
  eq: "equals",
  neq: "does not equal",
  in: "is any of",
  notIn: "is none of",
};

export const TICKET_KINDS = ["ticket", "task"];

// Template status (per request type) for the catalog list.
export const TEMPLATE_STATUS = {
  none: { label: "None", badge: "badge-muted" },
  draft: { label: "Draft", badge: "badge-warning" },
  active: { label: "Active", badge: "badge-success" },
};

// Small stable id for UI identity (deps keyed by identity survive key renames).
export function uid() {
  return Math.random().toString(36).slice(2, 10);
}

export function slugify(s) {
  return String(s || "")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase();
}

// Intake-value helpers (SOUND-508) — mirror the backend intakeValueValidator so
// the create modal shows/hides fields and enforces required-when-visible exactly
// as the server does (a field hidden by its predicate is not required, not sent).
export function isIntakeValueEmpty(v) {
  return v === undefined || v === null || (typeof v === "string" && v.trim() === "");
}

export function evaluateVisibleWhen(predicate, values = {}) {
  if (!predicate || !predicate.field) return true;
  const actual = values[predicate.field];
  const target = predicate.value;
  switch (predicate.operator) {
    case "eq":
      return actual === target;
    case "neq":
      return actual !== target;
    case "in":
      return Array.isArray(target) && target.includes(actual);
    case "notIn":
      return Array.isArray(target) && !target.includes(actual);
    default:
      return true; // unknown operator → treat as visible (backend rejects it)
  }
}
